import { type RefObject, useEffect } from 'react'

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

/** Trampa de foco para un modal/diálogo: mientras está `active`, Tab/
 *  Shift+Tab ciclan sólo entre los elementos enfocables de `ref` (nunca se
 *  escapa hacia el fondo de la página), y al desactivarse el foco vuelve a
 *  lo que estaba enfocado justo antes de abrir — típicamente el botón que
 *  disparó el diálogo.
 *
 *  Nada especial hace falta para modales anidados (StepUpDialog sobre el
 *  formulario que lo abrió, ver el comentario de useClickOutside): son
 *  árboles de DOM hermanos, no anidados, así que el `querySelectorAll`
 *  de cada trampa —scopeado a su propio `ref`— nunca ve los elementos del
 *  otro. Cada uno restaura el foco a lo que tenía antes de SU propia
 *  apertura, que es exactamente lo que se espera al cerrar el de encima.
 *
 *  Si algo adentro ya pidió foco por su cuenta (un botón con `autoFocus`,
 *  como el de confirmar en ConfirmDialog) no se le pisa: React aplica
 *  `autoFocus` en el commit, antes de que corra este efecto, así que
 *  `document.activeElement` ya apunta ahí cuando se revisa acá. */
export function useFocusTrap(ref: RefObject<HTMLElement | null>, active: boolean): void {
  useEffect(() => {
    const container = ref.current
    if (!active || !container) return

    const previouslyFocused = document.activeElement as HTMLElement | null

    function getFocusable(): HTMLElement[] {
      return Array.from(container!.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
    }

    if (!container.contains(document.activeElement)) {
      const [first] = getFocusable()
      ;(first ?? container).focus()
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Tab') return
      const focusable = getFocusable()
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (!first || !last) {
        event.preventDefault()
        return
      }
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      previouslyFocused?.focus()
    }
  }, [ref, active])
}
